import { apiPost } from './api';
import { auth } from './firebase';

// How long a signed-in session takes to put a tree on the screen.
//
// On 2026-09-21 an account signed in and waited roughly thirty seconds for a
// small tree to appear. The server logs answered the easy half of that
// immediately — no cold start, one warm instance running since the day before,
// the snapshot request served in 0.446s including a stale rebuild — and were
// silent on the half that mattered. Between the sign-in the API recorded and
// the first snapshot request it received, thirty seconds passed in which the
// browser asked the API for nothing at all. Whatever it was doing, it was doing
// with Firebase Auth and Firestore, neither of which passes through Cloud Run.
//
// So the server could prove where the delay was not, and had no way to say
// where it was. This module closes that: it times the stretch from "a signed-in
// user exists" to "the tree is on the screen", breaks it into named legs, and
// posts the numbers through the same path the client error reports already
// take, so they land in Cloud Logging beside the server's own structured logs.
//
// Everything here is best-effort and must never throw. A measurement that can
// break the thing it measures is worse than no measurement.

// Marks are milliseconds since `t0`, which is the moment a signed-in user first
// appears to the app. That is the client-side twin of the server's "Recorded
// sign-in" line, which makes the two timelines comparable without a shared
// clock: both sides are measuring from the same event, each in its own time.
let t0 = null;
let marks = null;
let context = null;
let sent = false;
let watchdog = null;

// Long enough that a slow-but-working load reports itself as a load rather than
// a stall, short enough that somebody who gave up and closed the tab has
// already been counted. The case this exists for is the one where the tree
// never arrives: without a timer that session reports nothing, and a load that
// hangs forever would be invisible in exactly the way this module exists to fix.
const STALL_AFTER_MS = 20000;

const now = () => {
  try {
    return typeof performance !== 'undefined' && performance.now
      ? performance.now()
      : Date.now();
  } catch {
    return Date.now();
  }
};

// Page-load context, read once when the session begins.
//
// Without it a slow number is ambiguous: eight seconds to first tree means one
// thing on a cold page load over a bad connection and something quite different
// on a tab that has been open for an hour. `pageAgeAtSignInMs` separates a
// session restored during page load from an interactive sign-in on a warm page
// without needing a heuristic to guess which happened.
function pageContext() {
  const out = {};
  try {
    out.pageAgeAtSignInMs = Math.round(now());
    const nav = performance.getEntriesByType?.('navigation')?.[0];
    if (nav) {
      out.navType = nav.type || null;
      out.domContentLoadedMs = Math.round(nav.domContentLoadedEventEnd || 0);
      out.pageLoadMs = Math.round(nav.loadEventEnd || 0);
    }
    const conn = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
    if (conn) {
      out.effectiveType = conn.effectiveType || null;
      out.downlinkMbps = typeof conn.downlink === 'number' ? conn.downlink : null;
      out.rttMs = typeof conn.rtt === 'number' ? conn.rtt : null;
    }
  } catch {
    // Any of these is optional; a browser that withholds one still reports the
    // legs, which are the part worth having.
  }
  return out;
}

/**
 * Starts a measurement. Called when a signed-in user first appears.
 *
 * Re-entrant on purpose: Firebase fires `onAuthStateChanged` more than once for
 * the same person, and the first call is the one that matches what the user
 * experienced. Later calls for a session already being measured are ignored
 * rather than restarting the clock, which would hide the very delay this is for.
 */
export function beginLoadSession(reason) {
  try {
    if (t0 !== null && !sent) return;
    t0 = now();
    marks = {};
    context = { reason: reason || 'unknown', ...pageContext() };
    sent = false;
    clearTimeout(watchdog);
    watchdog = setTimeout(() => { send('stalled'); }, STALL_AFTER_MS);
  } catch {
    // Leave the module inert rather than half-armed.
  }
}

/** Records a named leg. Unknown or duplicate names are kept as-is; the server clamps. */
export function mark(name) {
  try {
    if (t0 === null || sent || !marks) return;
    marks[name] = Math.round(now() - t0);
  } catch {
    // ignore
  }
}

/** Adds descriptive context to the report, such as the tree and its size. */
export function markContext(fields) {
  try {
    if (t0 === null || sent || !context) return;
    Object.assign(context, fields || {});
  } catch {
    // ignore
  }
}

/**
 * Sends the measurement, once.
 *
 * `outcome` is what happened to the load: it arrived, it failed, or it never
 * finished and the watchdog gave up. Those three want to be told apart in a
 * query, because a fast failure and a fast success look identical if all you
 * have is a duration.
 */
function send(outcome) {
  try {
    if (sent || t0 === null) return;
    sent = true;
    clearTimeout(watchdog);
    watchdog = null;

    // The endpoint is authenticated, so there is no point trying without a user.
    if (!auth.currentUser) return;

    const totalMs = Math.round(now() - t0);
    apiPost('/api/client-timing', {
      metric: 'tree-first-load',
      outcome,
      totalMs,
      marks,
      ...context,
      url: typeof window !== 'undefined' ? window.location.href : null,
      userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : null,
    }).catch(() => {
      // A failed report is not worth a retry. Retrying a reporter that cannot
      // reach the server is how one slow session becomes a request storm, and
      // the thing being reported is already over.
    });
  } catch {
    // ignore
  }
}

/** The tree is on the screen. */
export function finishLoadSession(outcome) {
  send(outcome || 'loaded');
}

/**
 * Drops the measurement without sending. Used on sign-out, where the load did
 * not finish because the user left, which is not a number worth keeping.
 */
export function abandonLoadSession() {
  try {
    clearTimeout(watchdog);
    watchdog = null;
    t0 = null;
    marks = null;
    context = null;
    sent = false;
  } catch {
    // ignore
  }
}
